/**
 * @voltxampere/components — MCU execution component definition (Sprint 46)
 *
 * Sprint 46 ADR-0067 introduces the `mcu-execution` canonical engine
 * type. This component drives runtime AVR-8bit emulation (ATmega328P
 * / ATmega2560 / ATtiny85 family). Per Sprint 46 split (Şefim onayı,
 * Yol B), Sprint 46 ships infrastructure ONLY — stamp scaffold +
 * standalone AVR emulator + Intel HEX parser. UI variant migration +
 * Inspector runtime panel + 3 frozen sentinels land in Sprint 47.
 *
 * Pin layout (Sprint 46 MVP — ATmega328P DIP-28 subset):
 *
 *   vcc, gnd        — supply rails (5 V default)
 *   pb0..pb7        — Port B (8 GPIO), includes PB5 (Arduino LED_BUILTIN)
 *
 * ATmega2560 (86 GPIO) and ATtiny85 (6 GPIO) family-specific pin
 * layouts ship in Sprint 47 — that sprint adds family-aware pin
 * overrides + UI variant pin maps. Sprint 46 default works for all
 * three because the stamp's per-pin loop reads `gpioPinCount` from
 * params and only addresses pins inside that count.
 *
 * Flash code is stored as the raw Intel HEX text (string) for schema
 * serialisation friendliness. Sprint 46-5 parseIntelHex converts the
 * string to `Uint8Array` flash bytes at solver time. Empty string is
 * the legitimate default (placeholder MCU pre-.hex-upload).
 *
 * Engine extension #4 — engine zero-diff cumulative invariant
 * intentionally broken vs Sprint 45 baseline; new baseline is the
 * Sprint 46 merge SHA. Sprint 47+ verifies against Sprint 46 SHA.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { z } from "zod";
export const McuExecutionParamsSchema = z
    .object({
    /** AVR family — drives default flash/ram/pin sizes. */
    family: z.enum(["atmega328p", "atmega2560", "attiny85"]).default("atmega328p"),
    /** CPU clock frequency (Hz). Default 16 MHz (Arduino UNO). */
    clockHz: z.number().positive().finite().default(16_000_000),
    /**
     * Pre-compiled Intel HEX text. Empty string is the legitimate
     * placeholder default — emulator no-ops and all GPIO pins read
     * as inputs (no augmented row stamped). User uploads .hex via
     * Inspector runtime panel (Sprint 47).
     */
    flashHex: z.string().default(""),
    /** Flash size override (bytes). Defaults from `family` if unset. */
    flashSize: z.number().positive().int().finite().optional(),
    /** SRAM size override (bytes). Defaults from `family` if unset. */
    ramSize: z.number().positive().int().finite().optional(),
    /** GPIO pin count override. Defaults from `family` if unset. */
    gpioPinCount: z.number().positive().int().finite().optional(),
    /** ADC channel count override. Defaults from `family` if unset. */
    adcPinCount: z.number().nonnegative().int().finite().optional(),
    /** PWM pin count override. Defaults from `family` if unset. */
    pwmPinCount: z.number().nonnegative().int().finite().optional(),
    /**
     * Instructions executed per simulator tick. Higher = faster but
     * coarser per-frame granularity. Default 1000 — about 60 µs of
     * AVR time per browser frame at 16 MHz. UI slider 100-10000 in
     * Sprint 47.
     */
    instructionsPerTick: z.number().positive().int().finite().default(1000),
    /** Supply voltage (V). Default 5 V. */
    vSupply: z.number().positive().finite().default(5),
})
    .strict();
/**
 * Sprint 46 MVP pin layout — ATmega328P-style DIP-28 subset:
 * `vcc`, `gnd`, plus 8 Port B GPIO pins (pb0..pb7). PB5 is the
 * Arduino UNO LED_BUILTIN pin (digital pin 13 on UNO silkscreen).
 *
 * Sprint 47 extends to family-aware Port C / Port D plus ADC pins.
 */
const mcuPins = [
    { id: "vcc", side: "left", offset: -1, label: "VCC" },
    { id: "gnd", side: "left", offset: 8, label: "GND" },
    { id: "pb0", side: "right", offset: 0, label: "PB0" },
    { id: "pb1", side: "right", offset: 1, label: "PB1" },
    { id: "pb2", side: "right", offset: 2, label: "PB2" },
    { id: "pb3", side: "right", offset: 3, label: "PB3" },
    { id: "pb4", side: "right", offset: 4, label: "PB4" },
    { id: "pb5", side: "right", offset: 5, label: "PB5" },
    { id: "pb6", side: "right", offset: 6, label: "PB6" },
    { id: "pb7", side: "right", offset: 7, label: "PB7" },
];
export const mcuExecutionDefinition = {
    type: "mcu-execution",
    category: "active",
    displayName: { tr: "MCU (Yürütme)", en: "MCU (Execution)" },
    symbol: "U",
    pins: mcuPins,
    parameterSchema: McuExecutionParamsSchema,
    // Stamp evaluates per Newton iteration — output voltages depend on
    // the current xPrev iterate (input pin reads). Marking nonlinear
    // ensures the solver iterates rather than assuming a fixed-point
    // converged on the first call.
    nonlinear: true,
};
//# sourceMappingURL=mcu-execution.js.map